import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { calculateCanvasWidth, setupCanvasForHighDPI } from './canvasUtils';

const RulerContainer = styled.div`
  position: relative;
  height: 40px;
  background: #2a2a2a;
  border-bottom: 1px solid #444;
  overflow: hidden;
  user-select: none;
`;

const RulerCanvasContainer = styled.div.attrs<{ $visibleStartTime: number; $pixelsPerSecond: number }>(props => ({
  style: {
    left: `${props.$visibleStartTime * props.$pixelsPerSecond}px`
  }
}))`
  position: absolute;
  top: 0;
  bottom: 0;
`;

const RulerCanvas = styled.canvas`
  display: block;
  cursor: crosshair;
`;

const PlayheadContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`;

const Playhead = styled.div.attrs<{ $position: number }>(props => ({
  style: {
    left: `${props.$position}px`
  }
}))`
  position: absolute;
  top: 0;
  width: 2px;
  height: 100%;
  background: #ff4444;
  box-shadow: 0 0 4px rgba(255, 68, 68, 0.5);
  transform: translateX(-1px);
`;

const TimeTooltip = styled.div.attrs<{ $visible: boolean; $x: number; $y: number }>(props => ({
  style: {
    left: `${props.$x}px`,
    top: `${props.$y}px`,
    opacity: props.$visible ? 1 : 0
  }
}))`
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-family: monospace;
  pointer-events: none;
  z-index: 20;
  white-space: nowrap;
  transition: opacity 0.2s ease;
  transform: translate(-50%, -100%);
`;

interface TimelineRulerProps {
  width: number;
  height?: number;
  duration: number;
  currentTime: number;
  pixelsPerSecond: number;
  onSeek?: (time: number) => void;
  viewportRef?: React.RefObject<HTMLDivElement>;
  className?: string;
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Canvas utilities are now imported from canvasUtils.ts

export const TimelineRuler: React.FC<TimelineRulerProps> = ({
  width,
  height = 40,
  duration,
  currentTime,
  pixelsPerSecond,
  onSeek,
  viewportRef,
  className
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    time: string;
  }>({ visible: false, x: 0, y: 0, time: '' });

  // Calculate visible time range for ruler
  const [visibleStartTime, setVisibleStartTime] = React.useState(0);
  const [visibleEndTime, setVisibleEndTime] = React.useState(duration);

  // Set up viewport tracking for ruler
  useEffect(() => {
    if (!viewportRef?.current) {
      setVisibleStartTime(0);
      setVisibleEndTime(duration);
      return;
    }

    const viewport = viewportRef.current;
    const trackHeaderWidth = 185; // Width of track header
    
    // Adaptive buffer based on zoom level
    const getAdaptiveBuffer = (pixelsPerSecond: number) => {
      if (pixelsPerSecond > 200) return 60; // 60 seconds at extreme zoom
      if (pixelsPerSecond > 100) return 45; // 45 seconds at high zoom
      if (pixelsPerSecond > 50) return 30;  // 30 seconds at medium zoom
      return 20; // 20 seconds at low zoom
    };
    
    const bufferTime = getAdaptiveBuffer(pixelsPerSecond);

    const updateVisibleRange = () => {
      const scrollLeft = viewport.scrollLeft;
      const viewportWidth = viewport.clientWidth;

      // Calculate visible time range with adaptive buffer
      const startTime = Math.max(0, (scrollLeft - trackHeaderWidth) / pixelsPerSecond - bufferTime);
      const endTime = Math.min(duration, (scrollLeft + viewportWidth - trackHeaderWidth) / pixelsPerSecond + bufferTime);

      // Only update if the change is significant (more than 1 second or 10% of buffer)
      const threshold = Math.min(1, bufferTime * 0.1);
      if (Math.abs(startTime - visibleStartTime) > threshold || 
          Math.abs(endTime - visibleEndTime) > threshold) {
        setVisibleStartTime(startTime);
        setVisibleEndTime(endTime);
      }
    };

    updateVisibleRange();

    // Throttle scroll updates to avoid excessive redraws
    let scrollTimeout: NodeJS.Timeout | null = null;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateVisibleRange, 16); // ~60fps throttling
    };

    viewport.addEventListener('scroll', handleScroll);
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [viewportRef, pixelsPerSecond, duration]);

  const drawRuler = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Calculate visible portion parameters
    const visibleDuration = visibleEndTime - visibleStartTime;
    const canvasWidth = calculateCanvasWidth(visibleDuration, pixelsPerSecond);
    
    // Set up canvas for high-DPI rendering
    const ctx = setupCanvasForHighDPI(canvas, canvasWidth, height);

    // Clear canvas
    ctx.fillStyle = '#2a2a2a';
    ctx.fillRect(0, 0, canvasWidth, height);

    // Draw time markers - ensure proper scaling for high-DPI displays
    const dpr = window.devicePixelRatio || 1;
    ctx.fillStyle = '#888';
    ctx.font = `${11 * Math.max(1, dpr * 0.75)}px monospace`; // Scale font for readability on retina
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const timeStep = pixelsPerSecond < 20 ? 10 : // 10 second intervals when zoomed out
                     pixelsPerSecond < 50 ? 5 : // 5 second intervals
                     pixelsPerSecond < 100 ? 2 : // 2 second intervals
                     pixelsPerSecond < 200 ? 2 : // 2 second intervals
                     1; // 1 second intervals when zoomed in

    // Calculate scale factor if canvas is truncated
    const desiredRulerWidth = visibleDuration * pixelsPerSecond;
    const scaleX = canvasWidth / desiredRulerWidth;
    const isCanvasTruncated = canvasWidth < desiredRulerWidth;
    
    // Find the first time mark that should be visible
    const startTime = Math.floor(visibleStartTime / timeStep) * timeStep;
    
    for (let time = startTime; time <= visibleEndTime; time += timeStep) {
      if (time < 0) continue;
      
      // Convert time to canvas x position (relative to visible start)
      const rawX = (time - visibleStartTime) * pixelsPerSecond;
      const x = isCanvasTruncated ? rawX * scaleX : rawX;
      
      if (x < 0 || x > canvasWidth) continue;

      // Draw major tick marks - ensure line width is visible on retina
      ctx.strokeStyle = '#666';
      ctx.lineWidth = Math.max(1, dpr * 0.5); // Scale line width for retina displays
      ctx.beginPath();
      ctx.moveTo(x, height - 15);
      ctx.lineTo(x, height);
      ctx.stroke();

      // Draw time labels
      if (time % (timeStep * 2) === 0 || pixelsPerSecond > 100) {
        ctx.fillText(formatTime(time), x, height - 25);
      }

      // Draw minor tick marks between major ones
      if (pixelsPerSecond > 50 && time + timeStep / 2 <= visibleEndTime) {
        const minorTime = time + timeStep / 2;
        const rawMinorX = (minorTime - visibleStartTime) * pixelsPerSecond;
        const minorX = isCanvasTruncated ? rawMinorX * scaleX : rawMinorX;
        if (minorX > 0 && minorX <= canvasWidth) {
          ctx.strokeStyle = '#444';
          ctx.lineWidth = Math.max(0.5, dpr * 0.25); // Thinner line for minor ticks
          ctx.beginPath();
          ctx.moveTo(minorX, height - 8);
          ctx.lineTo(minorX, height);
          ctx.stroke();
        }
      }
    }

    // Draw duration boundary if it's visible
    if (duration >= visibleStartTime && duration <= visibleEndTime) {
      const rawDurationX = (duration - visibleStartTime) * pixelsPerSecond;
      const durationX = isCanvasTruncated ? rawDurationX * scaleX : rawDurationX;
      if (durationX >= 0 && durationX <= canvasWidth) {
        ctx.strokeStyle = '#999';
        ctx.lineWidth = Math.max(2, dpr); // Scale duration boundary line
        ctx.beginPath();
        ctx.moveTo(durationX, 0);
        ctx.lineTo(durationX, height);
        ctx.stroke();
      }
    }

  }, [height, duration, pixelsPerSecond, visibleStartTime, visibleEndTime]);

  useEffect(() => {
    drawRuler();
  }, [drawRuler]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const time = Math.max(0, Math.min(duration, x / pixelsPerSecond));
    
    setTooltip({
      visible: true,
      x,
      y: -8,
      time: formatTime(time)
    });
  }, [duration, pixelsPerSecond]);

  const handleMouseLeave = useCallback(() => {
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!onSeek) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const time = Math.max(0, Math.min(duration, x / pixelsPerSecond));
    onSeek(time);
  }, [duration, pixelsPerSecond, onSeek]);

  const playheadPosition = currentTime * pixelsPerSecond;

  return (
    <RulerContainer
      ref={containerRef}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <RulerCanvasContainer
        $visibleStartTime={visibleStartTime}
        $pixelsPerSecond={pixelsPerSecond}
      >
        <RulerCanvas
          ref={canvasRef}
        />
      </RulerCanvasContainer>
      <PlayheadContainer>
        <Playhead $position={playheadPosition} />
      </PlayheadContainer>
      <TimeTooltip
        $visible={tooltip.visible}
        $x={tooltip.x}
        $y={tooltip.y}
      >
        {tooltip.time}
      </TimeTooltip>
    </RulerContainer>
  );
};

export default TimelineRuler;